import { OrgMember, OrgProject, ReportRow, WeekEntry, WeekSheet } from '../../../model/organization.model';

// Mock data for UI demonstration — no endpoints exist yet.

export const members: OrgMember[] = [
  { id: 'anna', name: 'Anna Bennett', team: 'Design', role: 'member' },
  { id: 'marcus', name: 'Marcus Reid', team: 'Engineering', role: 'member' },
  { id: 'oliver', name: 'Oliver Grant', team: 'Engineering', role: 'member' },
  { id: 'maria', name: 'Maria Lopez', team: 'Management', role: 'manager' },
  { id: 'peter', name: 'Peter Shaw', team: 'Engineering', role: 'member' },
  { id: 'dylan', name: 'Dylan Ross', team: 'QA', role: 'member' },
];

export const projects: OrgProject[] = [
  { id: 'acme-redesign', client: 'Acme', name: 'Website redesign', color: '#b4674d', billable: true, rate: 120, budgetHours: 400 },
  { id: 'acme-support', client: 'Acme', name: 'Support', color: '#b4674d', billable: true, rate: 95, budgetHours: 200 },
  { id: 'bolt-mobile', client: 'Bolt', name: 'Mobile app', color: '#4d7ab4', billable: true, rate: 140, budgetHours: 500 },
  { id: 'nova-audit', client: 'Nova', name: 'Process audit', color: '#7a5cb4', billable: true, rate: 160, budgetHours: 200 },
  { id: 'internal', client: 'Internal', name: 'Operations', color: '#6b7a5c', billable: false, rate: 0 },
];

export const memberById = (id: string) => members.find(member => member.id === id);
export const projectById = (id: string) => projects.find(project => project.id === id);

// ---------------------------------------------------------------------------
// Approvals — one week
// ---------------------------------------------------------------------------

export const weekSheets: WeekSheet[] = [
  { memberId: 'anna', status: 'submitted', submittedAt: 'Friday, 18:02' },
  { memberId: 'marcus', status: 'submitted', submittedAt: 'Friday, 19:40' },
  { memberId: 'oliver', status: 'submitted', submittedAt: 'Saturday, 11:15' },
  { memberId: 'maria', status: 'approved', submittedAt: 'Friday, 17:30' },
  {
    memberId: 'dylan',
    status: 'rejected',
    submittedAt: 'Friday, 20:10',
    reviewNote: 'Monday and Tuesday are 10 hours each — confirm the overtime before this is approved.',
  },
  { memberId: 'peter', status: 'open' },
];

const entry = (
  memberId: string,
  projectId: string,
  workType: string,
  billable: boolean,
  hours: number[],
): WeekEntry => ({ memberId, projectId, workType, billable, hours });

export const weekEntries: WeekEntry[] = [
  entry('anna', 'acme-redesign', 'Design', true, [6, 5, 4, 6, 3, 0, 0]),
  entry('anna', 'acme-redesign', 'Meetings', false, [0, 1, 1, 0, 1, 0, 0]),
  entry('anna', 'bolt-mobile', 'Design', true, [2, 2, 3, 2, 4, 0, 0]),

  entry('marcus', 'bolt-mobile', 'Engineering', true, [8, 7, 8, 6, 5, 0, 0]),
  entry('marcus', 'internal', 'Admin', false, [0, 1, 0, 2, 1.5, 0, 0]),

  entry('oliver', 'acme-support', 'Engineering', true, [8, 8, 8, 8, 6, 0, 0]),
  entry('oliver', 'acme-support', 'QA', true, [0, 0, 0, 0, 4, 0, 0]),

  entry('maria', 'nova-audit', 'Audit', true, [8, 8, 8, 8, 8, 0, 0]),

  entry('dylan', 'bolt-mobile', 'QA', true, [10, 10, 8, 8, 8, 0, 0]),
];

// ---------------------------------------------------------------------------
// Reports — one month
// ---------------------------------------------------------------------------

const reportRow = (
  memberId: string,
  projectId: string,
  workType: string,
  hours: number,
  billableHours: number,
): ReportRow => ({ memberId, projectId, workType, hours, billableHours });

export const reportRows: ReportRow[] = [
  reportRow('anna', 'acme-redesign', 'Design', 96, 96),
  reportRow('anna', 'acme-redesign', 'Meetings', 12, 0),
  reportRow('anna', 'bolt-mobile', 'Design', 52, 52),

  reportRow('marcus', 'bolt-mobile', 'Engineering', 136, 136),
  reportRow('marcus', 'internal', 'Admin', 18, 0),

  reportRow('oliver', 'acme-support', 'Engineering', 152, 152),
  reportRow('oliver', 'acme-support', 'QA', 16, 16),

  reportRow('maria', 'nova-audit', 'Audit', 160, 160),

  reportRow('peter', 'bolt-mobile', 'Engineering', 88, 88),
  reportRow('peter', 'internal', 'Learning', 24, 0),

  reportRow('dylan', 'bolt-mobile', 'QA', 120, 120),
  reportRow('dylan', 'acme-support', 'QA', 24, 24),
];

export const revenueOf = (row: ReportRow) => row.billableHours * (projectById(row.projectId)?.rate ?? 0);

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export const formatHours = (value: number) =>
  (Math.round(value * 100) / 100).toFixed(2).replace(/\.?0+$/, '') || '0';

export const formatMoney = (value: number) => `$${Math.round(value).toLocaleString('en-US')}`;

export const formatPercent = (part: number, total: number) =>
  total > 0 ? `${Math.round((part / total) * 100)}%` : '—';
